import { Box, Button, Checkbox, createTheme, TextField, Typography, useTheme } from '@mui/material';
import React, { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import SurveyService from '../../services/SurveyService';
import { useTranslation } from 'react-i18next';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Autolink from '../Autolink/Autolink';
import { OpenInNew } from '@mui/icons-material';
import { useIsSurveyPreview } from '../../hooks/useIsSurveyPreview';

interface Props {
  companyLogo: string;
  primaryColor: string;
  platform: string;
  selectedRating: number;
  secondaryColor: string;
  companyName: string;
  dataProtection: string;
  imprint: string;
  contactEmail: string;
  companyWebsiteUrl: string;
  customFont: string;
  title?: string;
  outroText?: string;
  contactForm?: boolean;
  introText?: string;
  id: string;
  onChange: (value: string) => void;
  onCheckPrivacyNotice: (value: boolean) => void;
  feedbackText?: string;
  feedbackTitle?: string;
  feedbackActive?: boolean;
  lastAnswerId?: string;
  shouldShowPrivacyNotice?: boolean;
  privacyNoticeText?: string;
  privacyNoticeLink?: string;
}

const NegativeFeedback: FC<Props> = ({
  primaryColor,
  secondaryColor,
  contactEmail,
  companyName,
  title,
  outroText,
  introText,
  customFont,
  id,
  selectedRating,
  onChange,
  feedbackText,
  feedbackTitle,
  feedbackActive,
  lastAnswerId,
  privacyNoticeText,
  privacyNoticeLink,
  shouldShowPrivacyNotice,
  onCheckPrivacyNotice,
}) => {
  const theme = createTheme();
  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation();
  const t = localContext ? localContext.t : globalT;
  const maxFeedbackChars = 300;
  const [feedback, setFeedback] = useState<string>('');
  const [sendFeedback, setSendFeedback] = useState<boolean>(false);
  const [privacyNoticeChecked, setPrivacyNoticeChecked] = useState<boolean>(false);
  const isPreview = useIsSurveyPreview();

  useEffect(() => {
    onChange(feedback);
  }, [feedback]);

  useEffect(() => {
    onCheckPrivacyNotice(privacyNoticeChecked);
  }, [privacyNoticeChecked]);
  const formatTextToString = (text: any) => {
    if (!text) return null;

    return text.replace(/(\r\n|\r|\n)/g, ' ');
  };

  const sendNpsSurvey = async () => {
    if (lastAnswerId) {
      try {
        if (isPreview) {
          setSendFeedback(true);
          return;
        }
        await SurveyService.updateNpsSurveyAnswer(
          lastAnswerId,
          id,
          selectedRating,
          contactEmail,
          formatTextToString(feedback),
          companyName,
          { has_accepted_privacy_notice: privacyNoticeChecked }
        );
        setSendFeedback(true);
      } catch (error) {
        console.error(error);
      }
    } else {
      console.log('No previous answer id found', lastAnswerId);
    }
  };

  const keyframes = {
    '@keyframes fadeIn': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
  };

  const handleFeedbackChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length <= maxFeedbackChars) {
      setFeedback(e.target.value);
    }
  };

  return (
    <Box
      className="survey-feedback survey-feedback--negative survey-feedback--nps"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <Typography
        className="survey-title survey-feedback-title"
        variant="h4"
        sx={{
          color: primaryColor,
          fontWeight: 'bold',
          textAlign: 'center',
          fontFamily: customFont,
        }}
      >
        {title}
      </Typography>
      <Typography
        className="survey-intro-text survey-feedback-intro-text"
        variant="h5"
        sx={{
          color: secondaryColor,
          fontWeight: 'light',
          fontSize: '1.1rem',
          mt: 4,
          mb: 4,
          lineHeight: '1.5',
          textAlign: 'center',
        }}
      >
        <Autolink text={introText} />
      </Typography>
      {companyName && outroText && (
        <Typography
          className="survey-outro-text"
          variant="h5"
          sx={{
            color: secondaryColor,
            fontWeight: 'light',
            mt: theme.spacing(1),
            mb: theme.spacing(4),
          }}
        >
          <Autolink text={outroText} />
        </Typography>
      )}
      {feedbackActive && (
        <Box className="survey-feedback-form">
          <Typography
            className="survey-feedback-label"
            variant="h6"
            sx={{
              color: secondaryColor,
              pt: theme.spacing(1),
              mb: theme.spacing(2),
            }}
          >
            {feedbackTitle}
          </Typography>
          <TextField
            className="survey-feedback-input"
            id="outlined-multiline-static"
            multiline
            rows={4}
            variant="outlined"
            placeholder={feedbackText}
            sx={{
              width: '96%',
            }}
            value={feedback}
            onChange={handleFeedbackChange}
          />
          <Typography
            className="survey-feedback-character-count"
            variant="body2"
            color="textSecondary"
            sx={{
              mb: theme.spacing(2),
              ml: theme.spacing(1),
              textAlign: 'left',
            }}
          >
            {feedback.length}/{maxFeedbackChars} {t('characters')}
          </Typography>
          {shouldShowPrivacyNotice && (
            <Box className="survey-privacy-notice">
              <Typography className="survey-privacy-notice-text">
                <Checkbox
                  className="survey-privacy-notice-checkbox"
                  checked={privacyNoticeChecked}
                  onChange={() => setPrivacyNoticeChecked((prev) => !prev)}
                ></Checkbox>
                {privacyNoticeText}{' '}
                <a
                  href={privacyNoticeLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  <OpenInNew
                    fontSize="small"
                    color="primary"
                  ></OpenInNew>
                </a>
              </Typography>
            </Box>
          )}
          <Button
            className="survey-button survey-button--send survey-button--nps-feedback"
            variant="contained"
            sx={{
              backgroundColor: primaryColor,
              color: 'white',
              width: 'fit-content',
              padding: theme.spacing(2),
              display: sendFeedback ? 'none' : 'inline-flex',
            }}
            disabled={shouldShowPrivacyNotice && !privacyNoticeChecked && !!feedback}
            onClick={() => sendNpsSurvey()}
          >
            {!sendFeedback ? t('send') : null}
          </Button>
          {sendFeedback && (
            <CheckCircleOutlineIcon
              fontSize={'large'}
              sx={{
                color: 'green',
                ...keyframes,
                animation: 'fadeIn 1s ease-out',
              }}
            />
          )}
        </Box>
      )}
      {contactEmail && !feedbackActive && (
        <Box
          className="survey-contact-email-wrapper"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: 'fit-content',
            mb: theme.spacing(1),
            mt: theme.spacing(2),
          }}
        >
          <Typography
            className="survey-contact-email"
            sx={{ fontWeight: 'bold' }}
          >{`${t('appreciate_feedback')}: ${contactEmail}`}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default NegativeFeedback;
